/**
 * Chapter 4 — gateways, texts and senders: the four strategies deal the same
 * six slots four ways (letters from the editor's own strategy cards, the
 * split from the engine's largest-remainder math), then texts rotate across
 * drops and the send options line up.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { AlertTriangle } from 'lucide-react';

import {
  STRATEGY_CARDS,
  gwColor,
  gwLetter,
} from '@/features/drop-generator/components/editor/gateways/cards/strategy-copy';
import { splitQuotas } from '@/features/drop-generator/components/editor/gateways/WeightsBar';

import { DEMO_OPTIONS, DEMO_ROUTES, DEMO_SENDER_GROUP, DEMO_SPLIT_WEIGHTS, DEMO_TEXT_GROUPS } from '../../fixtures';
import { Illustration } from '../../motion/Illustration';
import { Node } from '../../motion/Node';
import { tick } from '../../motion/use-motion';

const CYCLE = 12;
const SLOTS = 6;
const LIT = 'opacity-55 animate-dg-tut-step motion-reduce:animate-none motion-reduce:opacity-100';
const TITLE = 'text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';
type Card = (typeof STRATEGY_CARDS)[number];

/** The letters a strategy deals over the slots — from the editor's glyphs. */
function dealt(card: Card): string[] {
  if (card.glyph === 'bar')
    return splitQuotas(DEMO_SPLIT_WEIGHTS, SLOTS).flatMap((q, i) => Array.from({ length: q }, () => gwLetter(i)));
  return [...card.glyph].filter((ch) => ch !== '→').slice(0, SLOTS);
}

const Slot: FC<{ at: number; letter: string }> = ({ at, letter }) => (
  <span className={`flex h-5 min-w-5 items-center justify-center rounded px-1 font-mono text-[10px] font-bold ${LIT} ${letter === '!' ? 'text-warning' : 'text-white'}`}
    style={{ ...tick(at), backgroundColor: letter === '!' ? 'transparent' : gwColor(letter.charCodeAt(0) - 65) }}>
    {letter === '!' ? <AlertTriangle className="h-3.5 w-3.5" /> : letter}
  </span>
);

const StrategyRow: FC<{ card: Card }> = ({ card }) => (
  <div className="grid grid-cols-[110px_1fr] items-center gap-2 text-xs md:grid-cols-[110px_auto_1fr]">
    <span className="font-medium">{card.title}</span>
    <span className="flex items-center gap-1">
      {dealt(card).map((letter, i) => <Slot key={i} at={i} letter={letter} />)}
    </span>
    <span className="hidden text-muted-foreground md:block">{card.caption}</span>
  </div>
);

export const GatewaysIllustration: FC = () => (
  <Illustration cycleSeconds={CYCLE}>
    <p className={TITLE}>
      the pool — {DEMO_ROUTES.map((r, i) => `${gwLetter(i)} ${r.name}`).join(' · ')} — dealt over six drops
    </p>
    <div className="mt-2 flex flex-col gap-1.5">
      {STRATEGY_CARDS.map((card) => <StrategyRow key={card.value} card={card} />)}
    </div>
    <p className={`mt-3 ${TITLE}`}>texts — Rotate across the prompt groups</p>
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
      {DEMO_TEXT_GROUPS.concat(DEMO_TEXT_GROUPS).map((group, k) => (
        <Node key={k} at={SLOTS + 1 + k * 0.6}>
          <span className="text-muted-foreground">drop {k + 1}</span> → <span className="font-mono">{group}</span>
        </Node>
      ))}
      <Node at={SLOTS + 4}>senders from <span className="font-mono">{DEMO_SENDER_GROUP}</span> — Gateways tab</Node>
    </div>
    <p className={`mt-3 ${TITLE}`}>options — the Options tab</p>
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
      {DEMO_OPTIONS.map((o, i) => (
        <Node key={o.label} at={SLOTS + 5.5 + i * 0.6}>
          <span className="font-medium">{o.label}</span> <span className="text-muted-foreground">{o.value}</span>
        </Node>
      ))}
    </div>
  </Illustration>
);
